/**
 * @author: chenlv
 * @date: 2025/01/22
 * @description: Global Performance 全球表现卡片组件
 */
import React, { useRef, useMemo, useState, useImperativeHandle } from 'react';
import classnames from 'classnames';
import { cloneDeep } from 'lodash-es';
import Loading from '@intelligence/components/Loading';
import NoData from '@intelligence/components/NoData';
import CardSlotV2 from '@intelligence/components/CardSlotV2';
import {
  MapChart,
  LineChart,
  createMapChartTooltipFormatter,
  createLineChartTooltipFormatter,
} from '@intelligence/v3/components/ChartComponents';
import useIntlExtend from '@intelligence/hooks/useIntlExtend';
import {
  fetchCasualGenreProfileGlobalPerformanceMetricsSeparatedByMarket,
  fetchCasualGenreProfileGlobalPerformanceMetricsSeparatedByDateAndMarket,
} from '../../services';
import { isEmpty } from '@intelligence/v3/utils/common';
import { formatKMBWithPlaceholder } from '@intelligence/v3/utils/number';
import { getCountryNameFromCountryMap } from '@intelligence/v3/utils/region';
import { generateMetricKey } from '@intelligence/v3/utils/metric';
import { ChartType } from '@intelligence/v3/enums/chart';
import { METRIC_NAME_I18N_KEY_MAP } from '../../constants';

import type { TDataSourceType, TMetricType, IGenreModuleInstance, IGenreModuleUpdateParams } from '../../types';

/**
 * GlobalPerformanceCard 组件 Props
 */
interface IGlobalPerformanceCardProps extends IBaseProps {
  /** 当前选中的数据源 */
  dataSource: TDataSourceType;
  /** 当前选中的指标 */
  metric: TMetricType;
  /** 国家映射表 */
  countryMap: IRecord<IRegionCountryEntity>;
}

/**
 * Card组件右上角的操作按钮列表
 */
const ICON_LIST = [
  /** 世界地图 */
  { id: 1, value: ChartType.Map, context: 'dbi_world_map_chart', icon: 'chart-map' },
  /** 折线图 */
  { id: 2, value: ChartType.Line, context: 'dbi_line_chart', icon: 'discount-chart' },
];

/**
 * GlobalPerformanceCard 全球表现卡片组件
 * @description 展示全球各国家/地区的指标数据，支持世界地图和折线图两种展示方式
 */
const GlobalPerformanceCard: React.ForwardRefRenderFunction<IGenreModuleInstance, IGlobalPerformanceCardProps> = (
  props,
  ref,
) => {
  const { style, className, dataSource, metric, countryMap } = props;

  const { locale, formatMessage } = useIntlExtend();

  const cachedParams = useRef<IGenreModuleUpdateParams | null>(null);

  const [mapLoading, setMapLoading] = useState(true);
  const [lineLoading, setLineLoading] = useState(true);
  const [list, setList] = useState<ICasualGenreProfileMetricData[]>([]);
  const [separatedList, setSeparatedList] = useState<ICasualGenreProfileMetricData[]>([]);

  /**
   * 当前图表类型
   */
  const [chartType, setChartType] = useState<ChartType>(ChartType.Map);

  /**
   * 获取指标名称
   */
  const metricName = useMemo<string>(() => {
    const metricNameI18nKey = METRIC_NAME_I18N_KEY_MAP[metric];
    return formatMessage({ id: metricNameI18nKey });
  }, [locale, metric]);

  /**
   * 世界地图数据
   */
  const mapData = useMemo<IMapChartData[]>(() => {
    if (mapLoading) {
      return [];
    }

    if (isEmpty(list)) {
      return [];
    }

    const metricKey = generateMetricKey({ dataSource, metric });

    return list.map((item) => {
      const itemCountryCode = item?.market || '';
      const itemCountry = countryMap?.[itemCountryCode] || '';
      // 地图的 name 始终使用英文名称，以确保与 echarts 地图匹配
      // 翻译在 tooltip 中通过 payload.market 和 countryNameFormatter 处理
      const itemCountryName = itemCountry?.country_name_en || '';

      return {
        name: itemCountryName,
        value: (item?.[metricKey as keyof ICasualGenreProfileMetricData] as number) ?? Number.NaN,
        payload: { ...item },
      };
    });
  }, [mapLoading, countryMap, dataSource, metric, list]);

  /**
   * 折线图数据
   */
  const lineData = useMemo<ILineChartData[]>(() => {
    if (lineLoading) {
      return [];
    }

    if (isEmpty(separatedList)) {
      return [];
    }

    const metricKey = generateMetricKey({ dataSource, metric });

    return separatedList.map((item) => {
      return {
        date: item?.date || '',
        name: item?.market || '',
        value: (item?.[metricKey as keyof ICasualGenreProfileMetricData] as number) ?? Number.NaN,
        payload: { ...item },
      };
    });
  }, [lineLoading, dataSource, metric, separatedList]);

  /**
   * 切换图表类型
   */
  const handleChartTypeChange = (type: ChartType): void => {
    setChartType(type);

    if (ChartType.Map === type) {
      void updateList(cachedParams.current!);
    } else if (ChartType.Line === type) {
      void updateSeparatedList(cachedParams.current!);
    }
  };

  /**
   * 翻译国家名称（通过国家代码）
   */
  const formatCountryNameByCode = (countryCode: string): string => {
    return getCountryNameFromCountryMap(locale, countryCode, countryMap);
  };

  /**
   * 世界地图 Tooltip formatter
   */
  const mapTooltipFormatter = useMemo(() => {
    return createMapChartTooltipFormatter({
      metricName,
      countryNameFormatter: formatCountryNameByCode,
      valueFormatter: (value) => formatKMBWithPlaceholder(value),
      extraDataItems: [
        {
          label: formatMessage({ id: 'dbi_market_total_game' }),
          payloadKey: 'game_count',
          valueFormatter: (value) => formatKMBWithPlaceholder(value),
        },
      ],
    });
  }, [locale, countryMap, metricName]);

  /**
   * 折线图 Tooltip formatter
   */
  const lineTooltipFormatter = useMemo(() => {
    return createLineChartTooltipFormatter({
      metricName,
      seriesNameFormatter: formatCountryNameByCode,
      valueFormatter: (value) => formatKMBWithPlaceholder(value),
    });
  }, [locale, countryMap, metricName]);

  /**
   * 按 xxx 对 series 排序
   */
  const getSortedSeries = (series: any[]): any[] => {
    if (!Array.isArray(series) || series.length === 0) {
      return [];
    }

    const sortedSeries = cloneDeep(series);

    sortedSeries.sort((a, b) => {
      // TODO: 完善series排序
      const aIndex = ['']?.indexOf(a?.name as string) ?? -1;
      const bIndex = ['']?.indexOf(b?.name as string) ?? -1;
      return aIndex - bIndex;
    });

    return sortedSeries;
  };

  const updateList = async (params: IGenreModuleUpdateParams) => {
    setMapLoading(true);
    const { dataSource, metric, filterParams } = params || {};
    try {
      const res = await fetchCasualGenreProfileGlobalPerformanceMetricsSeparatedByMarket(
        dataSource,
        metric,
        filterParams,
      );
      setList(res?.data?.list || []);
    } catch (error) {
      console.error('fetchCasualGenreProfileGlobalPerformanceMetricsSeparatedByMarket error:', error);
      setList([]);
    } finally {
      setMapLoading(false);
    }
  };

  const updateSeparatedList = async (params: IGenreModuleUpdateParams) => {
    const { dataSource, metric, filterParams } = params || {};
    setLineLoading(true);
    try {
      const res = await fetchCasualGenreProfileGlobalPerformanceMetricsSeparatedByDateAndMarket(
        dataSource,
        metric,
        filterParams,
      );
      setSeparatedList(res?.data?.list || []);
    } catch (error) {
      console.error('fetchCasualGenreProfileGlobalPerformanceMetricsSeparatedByDateAndMarket error:', error);
      setSeparatedList([]);
    } finally {
      setLineLoading(false);
    }
  };

  const update = async (params: IGenreModuleUpdateParams) => {
    if (ChartType.Map === chartType) {
      void updateList(params);
    } else if (ChartType.Line === chartType) {
      void updateSeparatedList(params);
    }
  };

  /**
   * 组件刷新依赖项
   */
  const updateDeps = useMemo<React.DependencyList>(() => [], [metricName]);

  /**
   * 当前图表类型对应的加载状态
   */
  const currentLoading = chartType === ChartType.Map ? mapLoading : lineLoading;

  /**
   * 判断是否无数据
   */
  const currentNoData = chartType === ChartType.Map ? isEmpty(mapData) : isEmpty(lineData);

  useImperativeHandle(ref, () => ({
    update: (params: IGenreModuleUpdateParams) => {
      cachedParams.current = params ?? null;
      void update(params);
    },
  }));

  return (
    <CardSlotV2
      style={style}
      className={classnames('dbi-global-performance-card', className)}
      title={formatMessage({ id: 'dbi_market_global_perf' })}
      iconList={ICON_LIST}
      activeTab={chartType}
      getChange={handleChartTypeChange}
    >
      {currentLoading && (
        <div style={{ height: 400 }} className="dbi-global-performance-card__loading">
          <Loading content={formatMessage({ id: 'common_loading' })} />
        </div>
      )}
      {!currentLoading && currentNoData && (
        <div style={{ height: 400 }} className="dbi-global-performance-card__no-data">
          <NoData content={formatMessage({ id: 'common_nodata' })} />
        </div>
      )}
      {!currentLoading && !currentNoData && (
        <div className="dbi-global-performance-card__chart-wrapper">
          {/* 世界地图 */}
          {chartType === ChartType.Map && (
            <MapChart
              locale={locale}
              loading={currentLoading}
              data={mapData}
              tooltipFormatter={mapTooltipFormatter}
              updateDeps={updateDeps}
            />
          )}
          {/* 折线图 */}
          {chartType === ChartType.Line && (
            <LineChart
              locale={locale as any}
              loading={currentLoading}
              data={lineData}
              tooltipFormatter={lineTooltipFormatter}
              yAxisLabelFormatter={(value: number) => formatKMBWithPlaceholder(value)}
              legendFormatter={formatCountryNameByCode}
              getSortedSeries={getSortedSeries}
              updateDeps={updateDeps}
            />
          )}
        </div>
      )}
    </CardSlotV2>
  );
};

// @ts-ignore
export default React.forwardRef(GlobalPerformanceCard);
